import _ from 'lodash'
import { onClickOutside } from '@baserow/modules/core/utils/dom'

/**
 * helper function to extract options from element
 *
 * If binding provides .arg, the .arg property should be an object:
 *
 * {duration: Number, contentIsHtml: Bool, contentClasses: String}
 *
 * @param el
 * @param binding directive binding value
 * @returns {{duration: number, contentClasses: (*|null), value, contentType: (*|string)}}
 */
const getOptions = (el, binding) => {
  // defaults
  const tooltipOptions = {
    duration: 0,
    contentIsHtml: false,
    value: null,
    contentClasses: '',
  }

  if (binding.arg && _.isObject(binding.arg)) {
    _.merge(tooltipOptions, binding.arg)
  }
  tooltipOptions.value = binding.value
  return tooltipOptions
}

// make sure only one tooltip is visible
let currentTooltip = null
const switchToTooltip = (el) => {
  if (currentTooltip != null) {
    currentTooltip.tooltipClose()
  }
  currentTooltip = el
}

/**
 * This is a very simple and fast tooltip directive. It will add the binding value as
 * tooltip content. The tooltip only shows if there is a value.
 *
 * tooltip directive can be customized with arg, where arg is an object:
 * {
 *     // duration number of seconds the tooltip should be visible after pointer
 *     // leaves i icon or tooltip content
 *  duration: Number,
 *    // contentIsHtml informs the value is html and should not be escaped (sets .innerHtml directly)
 *  contentIsHtml: Bool,
 *    // contentClasses is a string with additional css classes for tooltip content container
 *  contentClasses: String
 *  }
 *
 * <i v-tooltip:[configObject]="someValue"/>
 */
export default {
  /**
   * If there is a value and the tooltip has not yet been initialized we can add the
   * mouse events to show and hide the tooltip.
   *
   */

  initialize(el, binding) {
    el.tooltipOptions = getOptions(el, binding)
    el.onClickOutsideCallback = null

    if (el._tooltipEventsAdded) {
      return
    }
    el._tooltipEventsAdded = true

    el.updatePositionEvent = (event) => {
      const rect = el.getBoundingClientRect()
      const position = el.getAttribute('tooltip-position') || 'bottom'
      const topValue = parseInt(el.getAttribute('tooltip-top-value') || '2')
      const rectTooltip = el.tooltipElement.getBoundingClientRect()

      /**
       * The centred positions (`top` and the `bottom` default) anchor on the
       * middle of the target, and `.tooltip--center` then pulls the box back by
       * half its own width. That is only inside the viewport while the target
       * has at least half a tooltip of room either side of it.
       *
       * A 32px control in the 52px collapsed sidebar does not: centring on
       * x=26 and shifting a 115px tooltip by -57px put it at x=-32, so a third
       * of the name was cut off by the edge of the window. The rail is where
       * the tooltip *is* the label, which makes losing it worse there than
       * anywhere else.
       *
       * Clamped so the shifted box stays on screen. The value returned is
       * still the pre-transform anchor, because the CSS applies the -50%.
       */
      const centreWithinViewport = (targetCentre) => {
        // An unmeasured tooltip cannot overflow anything, so there is nothing
        // to clamp against. This is the normal case on the first positioning
        // pass and in jsdom, where every element reports 0x0 -- clamping there
        // moved tooltips that were never off screen.
        if (rectTooltip.width === 0) {
          return targetCentre
        }

        const half = rectTooltip.width / 2
        const padding = 8
        const min = half + padding
        const max = window.innerWidth - half - padding

        // Only a tooltip that would actually leave the viewport is moved; one
        // that already fits stays anchored to the thing it labels.
        if (targetCentre >= min && targetCentre <= max) {
          return targetCentre
        }
        // `min` wins when the two bounds cross, i.e. a tooltip wider than the
        // viewport: keep its left edge on screen rather than its right.
        return targetCentre < min ? min : Math.max(min, max)
      }

      switch (position) {
        case 'top':
          el.tooltipElement.style.top =
            rect.top - topValue - rectTooltip.height + 'px'
          el.tooltipElement.style.left =
            centreWithinViewport(rect.left + rect.width / 2) + 'px'
          break
        case 'bottom-left':
          el.tooltipElement.style.top = rect.bottom + 4 + 'px'
          el.tooltipElement.style.left = rect.right - rectTooltip.width + 'px'
          el.tooltipElement.style.setProperty(
            '--tooltip-cursor-position-right',
            rect.width / 2 - 6 + 'px' // Middle of the main element
          )
          el.tooltipElement.style.setProperty(
            '--tooltip-cursor-position-left',
            'auto'
          )
          break
        case 'bottom-right':
          el.tooltipElement.style.top = rect.bottom + 4 + 'px'
          el.tooltipElement.style.left = rect.left + 'px'
          el.tooltipElement.style.setProperty(
            '--tooltip-cursor-position-left',
            rect.width / 2 + 'px' // Middle of the main element
          )
          el.tooltipElement.style.setProperty(
            '--tooltip-cursor-position-right',
            'auto'
          )
          break
        case 'bottom-cursor': {
          // A focus event carries no coordinates, so `event.x` is undefined and
          // the clamp below would resolve to NaN and drop the tooltip in the
          // corner. Falling back to the middle of the element is what
          // "at the cursor" means when there is no cursor.
          const cursorX = Number.isFinite(event?.x)
            ? event.x
            : rect.left + rect.width / 2
          el.tooltipElement.style.top = rect.bottom + 4 + 'px'
          el.tooltipElement.style.left =
            Math.max(
              rect.left + 6,
              Math.min(rect.left + rect.width - 6, cursorX)
            ) -
            rectTooltip.width / 2 +
            'px'
          break
        }
        default:
          el.tooltipElement.style.top = rect.bottom + 4 + 'px'
          el.tooltipElement.style.left =
            centreWithinViewport(rect.left + rect.width / 2) + 'px'
      }
    }
    el.removeTimeout = () => {
      if (el.tooltipTimeout) {
        clearTimeout(el.tooltipTimeout)
      }
      el.tooltipTimeout = null
    }

    // Cancels a pending "show" that hasn't fired yet. Used when the pointer
    // leaves before the show-delay elapses, so sweeping across dense toolbars
    // doesn't flicker a tooltip for every element passed over.
    el.removeShowTimeout = () => {
      if (el.tooltipShowTimeout) {
        clearTimeout(el.tooltipShowTimeout)
      }
      el.tooltipShowTimeout = null
    }

    el.tooltipMouseMoveEvent = (event) => {
      const position = el.getAttribute('tooltip-position') || 'bottom'
      if (position === 'bottom-cursor' && el.tooltipElement) {
        el.updatePositionEvent(event)
      }
    }

    // Short delay before the tooltip appears on hover-in. This prevents
    // flicker when the pointer sweeps across many elements quickly.
    const showDelay = 350

    el.tooltipMouseEnterEvent = (event) => {
      el.removeShowTimeout()
      // `event` is reused after the handler returns, so capture the coordinates
      // needed by the positioning logic before scheduling the delayed show.
      const { x, y } = event
      el.tooltipShowTimeout = setTimeout(() => {
        el.tooltipShowTimeout = null
        el.tooltipShow({ x, y })
      }, showDelay)
    }

    el.tooltipShow = (event) => {
      switchToTooltip(el)
      const position = el.getAttribute('tooltip-position') || 'bottom'
      const hide = el.getAttribute('hide-tooltip')

      if (hide) {
        return
      }

      if (!el.tooltipElement) {
        el.tooltipElement = document.createElement('div')

        const classes = ['tooltip', 'tooltip--body']
        if (position === 'top') {
          classes.push('tooltip--top')
        }

        if (position === 'top' || position === 'bottom') {
          classes.push('tooltip--center')
        }

        el.tooltipElement.className = classes.join(' ')
        document.body.insertBefore(el.tooltipElement, document.body.firstChild)

        el.tooltipContentElement = document.createElement('div')

        el.tooltipElement.appendChild(el.tooltipContentElement)
      }

      if (el.tooltipOptions.contentIsHtml) {
        el.tooltipContentElement.innerHTML = el.tooltipOptions.value
      } else {
        while (el.tooltipContentElement.firstChild) {
          el.tooltipContentElement.removeChild(
            el.tooltipContentElement.firstChild
          )
        }
        el.tooltipContentElement.appendChild(
          document.createTextNode(el.tooltipOptions.value)
        )
      }

      let contentClasses = ['tooltip__content']
      if (el.tooltipOptions.contentClasses) {
        contentClasses = contentClasses.concat(el.tooltipOptions.contentClasses)
      }
      el.tooltipContentElement.className = contentClasses.join(' ')

      el.updatePositionEvent(event)
      // we just entered, so we don't want any previously set timeout to close
      // the tooltip content
      el.removeTimeout()

      // make tooltip content preserved if pointer hovers
      // Only add event listeners if they haven't been added yet
      if (!el._tooltipContentEventsAdded) {
        el._tooltipContentEventsAdded = true
        el.tooltipContentElement.addEventListener(
          'pointerenter',
          el.removeTimeout
        )
        el.tooltipContentElement.addEventListener(
          'pointerleave',
          el.tooltipMoveLeaveEvent
        )
      }

      window.addEventListener('mousemove', el.tooltipMouseMoveEvent)
      window.addEventListener('scroll', el.updatePositionEvent, true)
      window.addEventListener('resize', el.updatePositionEvent)

      // refresh the callback - old callback should be removed because old instance
      // is may be longer present, and window object may still have click event handlers
      // that check if the pointer is in or out of tooltip content element (which itself
      // is long time gone)
      el.removeTooltipOutsideClickCallback()
      el.onClickOutsideCallback = onClickOutside(
        el.tooltipContentElement,
        el.tooltipClose
      )
    }
    /**
     * queue a close tooltip action.
     *
     * This can be called multiple times. Each call will postpone tooltipClose() call.
     * This way user can hover in and hover out several times and the tooltip still be
     * visible, if duration is > 0.
     */
    el.tooltipMoveLeaveEvent = () => {
      // Cancel any pending show so a quick hover-in/hover-out never surfaces
      // the tooltip.
      el.removeShowTimeout()
      el.removeTimeout()
      el.tooltipTimeout = setTimeout(
        el.tooltipClose,
        el.tooltipOptions.duration * 1000
      )
    }
    el.removeTooltipOutsideClickCallback = () => {
      if (el.onClickOutsideCallback) {
        el.onClickOutsideCallback()
        el.onClickOutsideCallback = null
      }
    }
    /**
     * actually closing the tooltip here
     */
    el.tooltipClose = () => {
      el.removeTooltipOutsideClickCallback()

      if (el.tooltipElement) {
        if (el.tooltipElement.parentNode) {
          el.tooltipElement.parentNode.removeChild(el.tooltipElement)
        }
        el.tooltipElement = null
        el.tooltipContentElement = null
        el._tooltipContentEventsAdded = false
      }

      window.removeEventListener('mousemove', el.tooltipMouseMoveEvent)
      window.removeEventListener('scroll', el.updatePositionEvent, true)
      window.removeEventListener('resize', el.updatePositionEvent)
      el.removeShowTimeout()
      el.removeTimeout()
    }

    // A touch tap synthesises `mouseenter` but never sends the matching
    // `mouseleave` -- the finger lifts and there is nothing left to leave. The
    // tooltip therefore opened on tap and stayed open forever, over the row it
    // described, and no gesture would dismiss it.
    //
    // `pointerType` tells a real pointer from a synthesised one, so the mouse
    // path is unchanged and touch simply does not open a tooltip. That is the
    // right answer rather than a workaround: a tooltip is hover-revealed help,
    // and on touch the same information has to be reachable another way (a long
    // name in this rail is an ellipsised label the user can widen the panel to
    // read) because there is no hover to reveal it with.
    el.tooltipPointerEnterEvent = (event) => {
      if (event.pointerType === 'touch') {
        return
      }
      el.tooltipMouseEnterEvent(event)
    }

    el.addEventListener('pointerenter', el.tooltipPointerEnterEvent)
    el.addEventListener('pointerleave', el.tooltipMoveLeaveEvent)
    // `pointercancel` matters on touch specifically: a pan or a long-press
    // menu takes the pointer away without ever sending `pointerleave`.
    el.addEventListener('pointercancel', el.tooltipMoveLeaveEvent)

    // KEYBOARD focus shows the tooltip too, not just hover. In a collapsed rail
    // the row's name exists ONLY as tooltip text -- the label is hidden and the
    // icon is all that renders -- so a hover-only tooltip left a keyboard user
    // tabbing through rows that are literally unidentifiable. WCAG 2.1 SC 1.4.13
    // asks that content shown on hover also be reachable on focus.
    //
    // `:focus-visible` is what makes it *keyboard* focus. A tap focuses the
    // button too, and that path reopened every tooltip touch was meant to be
    // spared: the finger lifts, no `focusout` follows because focus is still on
    // the button, and the tooltip stays up until something else takes focus
    // away. Reported as the panel toggle's tooltip appearing the moment the
    // panel is expanded and never leaving. `:focus-visible` is false for a tap
    // and true for Tab, which is exactly the distinction this needs.
    //
    // `focusin`/`focusout` rather than `focus`/`blur`, because those two do not
    // bubble: several call sites put `v-tooltip` on a wrapper whose focusable
    // element is a child, and those would never have fired.
    // Records that the focus about to arrive came from a pointer, so the
    // `focusin` handler below can tell a tap from a Tab. `:focus-visible` is
    // the obvious test and is not usable here: the browser decides it from its
    // own heuristics, and a `focus()` call the app makes itself -- which is how
    // several of these controls take focus -- is treated as keyboard focus.
    // What the tooltip needs to know is whether a POINTER put the focus there,
    // and `pointerdown` firing immediately before `focusin` is exactly that.
    el.tooltipPointerDownEvent = () => {
      el.tooltipFocusFromPointer = true
      // Cleared on the next frame: by then the `focusin` this pointer caused
      // has been and gone, and a later Tab must not inherit the flag.
      window.requestAnimationFrame(() => {
        el.tooltipFocusFromPointer = false
      })
    }

    el.tooltipFocusEnterEvent = (event) => {
      if (el.tooltipFocusFromPointer) {
        return
      }
      el.tooltipMouseEnterEvent(event)
    }

    el.addEventListener('pointerdown', el.tooltipPointerDownEvent)
    el.addEventListener('focusin', el.tooltipFocusEnterEvent)
    el.addEventListener('focusout', el.tooltipMoveLeaveEvent)
  },

  /**
   * If there isn't a value or if the directive is unbinded the tooltipElement can
   * be destroyed if it wasn't already and all the events can be removed.
   */
  terminate(el) {
    if (el.tooltipElement && el.tooltipElement.parentNode) {
      el.tooltipElement.parentNode.removeChild(el.tooltipElement)
    }

    if (el._tooltipEventsAdded) {
      el.removeEventListener('pointerenter', el.tooltipPointerEnterEvent)
      el.removeEventListener('pointerleave', el.tooltipMoveLeaveEvent)
      el.removeEventListener('pointercancel', el.tooltipMoveLeaveEvent)
      el.removeEventListener('pointerdown', el.tooltipPointerDownEvent)
      el.removeEventListener('focusin', el.tooltipFocusEnterEvent)
      el.removeEventListener('focusout', el.tooltipMoveLeaveEvent)
      el._tooltipEventsAdded = false
    }

    // Make sure a scheduled show can never fire on a torn-down element.
    if (el.removeShowTimeout) {
      el.removeShowTimeout()
    }

    // Nor a scheduled hide. `tooltipClose` reads `el.tooltipElement`, which is
    // nulled two lines down, so a hide left pending here fires against a
    // half-torn-down element -- and with a non-zero `duration` that timer can
    // easily outlive the row it belonged to.
    if (el.removeTimeout) {
      el.removeTimeout()
    }

    el.tooltipElement = null
    el.tooltipContentElement = null
    el._tooltipContentEventsAdded = false

    window.removeEventListener('scroll', el.updatePositionEvent, true)
    window.removeEventListener('resize', el.updatePositionEvent)
  },
  beforeMount(el, binding) {
    el.tooltipElement = null
    el.tooltipContentElement = null
    binding.dir.updated(el, binding)
  },
  updated(el, binding) {
    const { value } = binding

    if (!!value && el.tooltipElement) {
      el.tooltipOptions = getOptions(el, binding)
    } else if (!!value && el.tooltipElement === null) {
      binding.dir.initialize(el, binding)
    } else if (!value) {
      binding.dir.terminate(el)
    }
  },
  unmounted(el, binding) {
    binding.dir.terminate(el)
  },
}
